import {
  builtinFitsMethod,
  defaultRecording,
  seedOffsets,
  type PrayerMethod,
  type PrayerSettings,
} from '@amumin/domain';

// Het concept op de pagina Gebedstijden (apps/web/src/pages/Extras.tsx; DESIGN §8.GB.13): puur,
// zodat het zonder browser te testen is (extrasDraft.test.ts).

/**
 * Drie-wegs samenvoegen (review e-ux r2): wat de ouder in het scherm veranderde sinds `base`,
 * blijft staan; de rest komt van de server (`next`, met de nieuwe version). Zo gaat een keuze die
 * de ouder maakte terwijl een opslag of upload onderweg was, nooit stil verloren.
 */
export function rebaseDraft(
  draft: PrayerSettings,
  base: PrayerSettings,
  next: PrayerSettings,
): PrayerSettings {
  const out: Record<string, unknown> = { ...next };
  const d = draft as unknown as Record<string, unknown>;
  const b = base as unknown as Record<string, unknown>;
  for (const key of Object.keys(next)) {
    if (key === 'version' || key === 'offsets_confirmed_at') continue;
    if (JSON.stringify(d[key]) !== JSON.stringify(b[key])) out[key] = d[key];
  }
  return out as unknown as PrayerSettings;
}

/** Verschilt het scherm van wat er opgeslagen is (version en bevestiging tellen niet mee)? */
export function draftDirty(draft: PrayerSettings, saved: PrayerSettings): boolean {
  const d = draft as unknown as Record<string, unknown>;
  const v = saved as unknown as Record<string, unknown>;
  return Object.keys(saved).some(
    (key) =>
      key !== 'version' &&
      key !== 'offsets_confirmed_at' &&
      JSON.stringify(d[key]) !== JSON.stringify(v[key]),
  );
}

/** Alle afwijkingen nul (het hele jaar en per maand)? */
function offsetsEmpty(plan: PrayerSettings['offsets']): boolean {
  const zero = (o: Readonly<Record<string, number>> | undefined) =>
    !o || Object.keys(o).every((k) => !o[k]);
  return (
    zero(plan.base) &&
    Object.keys(plan.months).every((k) => zero((plan.months as Record<string, never>)[k]))
  );
}

type Recording = PrayerSettings['recording'];

/** Wat de pagina weet over de opnames: welke ingebouwde er zijn, en welke Jafari opzij zette. */
export interface RecordingContext {
  /** Ids van de meegeleverde ingebouwde opnames (PrayerSettingsView.recordings, available). */
  available: ReadonlyArray<string>;
  /** De ingebouwde opname die vóór de overstap naar Jafari gekozen was (of null). */
  remembered: Recording | null;
}

/**
 * Opname bij een andere methode (review e r3, oprichtersbesluit "standaard ezan"). Naar Jafari: een
 * ingebouwde (soennitische) opname wordt het belletje. Terug van Jafari naar een soennitische
 * methode met het belletje: dan komt de opname terug die Jafari opzij zette, of anders de standaard
 * van die methode (builtin_a), als hij meegeleverd is. Onder Jafari kon de ouder alleen het
 * belletje of een eigen opname kiezen, dus het belletje was daar geen echte keuze; nooit stil
 * terugvallen op het belletje. Een eigen opname blijft altijd staan.
 */
export function recordingForMethod(
  s: PrayerSettings,
  method: PrayerMethod,
  rec: RecordingContext,
): Recording {
  if (!builtinFitsMethod(method)) {
    return s.recording.kind === 'builtin' ? { kind: 'tone' } : s.recording;
  }
  if (builtinFitsMethod(s.method) || s.recording.kind !== 'tone') return s.recording;
  const has = (id: string) => rec.available.indexOf(id) >= 0;
  if (rec.remembered && rec.remembered.kind === 'builtin' && has(rec.remembered.id)) {
    return { kind: 'builtin', id: rec.remembered.id };
  }
  const fallback = defaultRecording(method);
  return fallback.kind === 'builtin' && has(fallback.id)
    ? { kind: 'builtin', id: fallback.id }
    : { kind: 'tone' };
}

/**
 * Andere methode (review e-ux r2): voorgestelde afwijkingen horen bij één methode. Kwamen de
 * huidige afwijkingen uit een voorstel (of staan ze allemaal op nul), dan krijgt de nieuwe methode
 * haar eigen voorstel (Diyanet in Nederland: Akşam +2, Yatsı −5; de andere methoden niets), met de
 * bijbehorende notitie. Eigen afwijkingen van de ouder blijven staan.
 */
export function withMethod(
  s: PrayerSettings,
  method: PrayerMethod,
  countryCode: string | null,
  rec: RecordingContext = { available: [], remembered: null },
): PrayerSettings {
  const next: PrayerSettings = {
    ...s,
    method,
    recording: recordingForMethod(s, method, rec),
  };
  // Golf 6b: een ochtendopname uit de bibliotheek is soennitisch; onder Jafari dus geen. Klonk bij
  // Fajr de oproep, dan wordt het het belletje (de API weigert "oproep" zonder Fajr-opname).
  if (!builtinFitsMethod(method) && s.fajr_recording && s.fajr_recording.kind === 'builtin') {
    next.fajr_recording = null;
    if (s.sounds.fajr === 'adhan') next.sounds = { ...s.sounds, fajr: 'chime' };
  }
  if (s.offsets_seed || offsetsEmpty(s.offsets)) {
    const seeded = seedOffsets(method, countryCode);
    next.offsets = {
      base: { ...seeded.plan.base },
      months: { ...seeded.plan.months },
    } as PrayerSettings['offsets'];
    next.offsets_seed = seeded.seed;
  }
  return next;
}
